/**
 * Conversations API
 *
 * API functions for managing AI conversations and messages.
 */

import { api, ApiError, parseRetryAfter } from "./client";
import type {
  Conversation,
  ConversationId,
  ConversationWithMessages,
  ProjectId,
  Message,
  MessageId,
  CreateConversationRequest,
  SendMessageRequest,
  ChatViewContext,
  ChatViewContextPayload,
  ProposalId,
} from "./types";

/** Timeout for chat requests (2 minutes) to match backend Anthropic timeout */
const CHAT_TIMEOUT_MS = 130_000;

/** Response from list conversations endpoint */
interface ConversationsListResponse {
  conversations: Conversation[];
}

/** Response from chat endpoint */
export interface ChatResponse {
  message: Message;
  proposal_id?: ProposalId;
  has_proposal: boolean;
  suggest_new_conversation?: boolean;
  suggestion_reason?: string;
  generated_title?: string;
  /** Correlation ID from x-correlation-id response header, for issue reporting */
  correlation_id?: string;
}

/**
 * List all conversations for a project
 */
export async function listConversations(
  projectId: ProjectId,
): Promise<Conversation[]> {
  const response = await api.get<ConversationsListResponse>(
    `/projects/${projectId}/conversations`,
  );
  return response.conversations;
}

/**
 * Create a new conversation
 */
export async function createConversation(
  projectId: ProjectId,
  title?: string,
): Promise<Conversation> {
  const request: CreateConversationRequest = { title };
  return api.post<Conversation>(
    `/projects/${projectId}/conversations`,
    request,
  );
}

/**
 * Get a conversation with all its messages
 */
export async function getConversation(
  conversationId: ConversationId,
): Promise<ConversationWithMessages> {
  return api.get<ConversationWithMessages>(`/conversations/${conversationId}`);
}

/** Convert frontend ChatViewContext to wire-format payload. Returns undefined for "general". */
function toContextPayload(
  ctx: ChatViewContext,
): ChatViewContextPayload | undefined {
  switch (ctx.type) {
    case "work_item":
      return { type: "work_item", work_item_id: ctx.workItemId };
    case "backlog":
      return {
        type: "backlog",
        filter_type_ids: ctx.filters.typeIds,
        filter_status_ids: ctx.filters.statusIds,
        filter_search: ctx.filters.search,
        visible_item_ids: ctx.visibleItemIds,
        selected_item_ids: ctx.selectedItemIds,
        total_item_count: ctx.totalItemCount,
      };
    default:
      return undefined;
  }
}

/**
 * Send a message and get AI response
 *
 * This calls the chat endpoint which:
 * 1. Saves the user message
 * 2. Assembles context for AI
 * 3. Calls Anthropic API
 * 4. Returns text response or creates proposal
 */
export async function sendMessage(
  conversationId: ConversationId,
  content: string,
  viewContext?: ChatViewContext,
): Promise<ChatResponse> {
  const context = viewContext ? toContextPayload(viewContext) : undefined;
  const request: SendMessageRequest = {
    message: content,
    context,
  };

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);

  try {
    return await api.post<ChatResponse>(
      `/conversations/${conversationId}/chat`,
      request,
      { signal: controller.signal },
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, "Chat request timed out. Please try again.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/** Callbacks for streaming chat events */
export interface StreamCallbacks {
  /** Called for each text delta (token) from the AI */
  onTextDelta: (text: string) => void;
  /** Called when AI invokes tools and is processing them */
  onToolProcessing?: () => void;
}

/**
 * Send a message and stream the AI response via SSE.
 *
 * Uses POST /conversations/{id}/chat/stream which returns Server-Sent Events.
 * Text deltas are forwarded through callbacks as they arrive.
 * Returns the final ChatResponse when streaming completes.
 *
 * @param signal - Optional AbortSignal for caller-driven cancellation (e.g. stop button).
 *                 The internal timeout still applies independently.
 */
export async function sendMessageStreaming(
  conversationId: ConversationId,
  content: string,
  viewContext: ChatViewContext | undefined,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<ChatResponse> {
  const context = viewContext ? toContextPayload(viewContext) : undefined;
  const body: SendMessageRequest = { message: content, context };

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);

  // Link external signal: if the caller aborts, abort our internal controller too
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }
  }

  try {
    const response = await fetch(
      `/api/conversations/${conversationId}/chat/stream`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new ApiError(
        response.status,
        (data as { error?: string })?.error ?? response.statusText,
        data,
        parseRetryAfter(response.headers.get("Retry-After")),
      );
    }

    if (!response.body) {
      throw new ApiError(500, "No response body for streaming");
    }

    const correlationId = response.headers.get("x-correlation-id") ?? undefined;
    const result = await parseSSEStream(response.body, callbacks);
    return { ...result, correlation_id: correlationId };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Distinguish caller-driven cancel from timeout
      if (signal?.aborted) {
        throw new ApiError(499, "Request cancelled by user.");
      }
      throw new ApiError(408, "Chat request timed out. Please try again.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Parse an SSE stream from a ReadableStream, invoking callbacks and returning
 * the final ChatResponse from the message_complete event.
 */
async function parseSSEStream(
  body: ReadableStream<Uint8Array>,
  callbacks: StreamCallbacks,
): Promise<ChatResponse> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: ChatResponse | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events (separated by double newlines)
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? ""; // Last element may be incomplete

      for (const part of parts) {
        if (!part.trim()) continue;

        let eventType = "";
        let data = "";

        for (const line of part.split("\n")) {
          if (line.startsWith("event:")) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            // Per SSE spec: remove at most one leading space after "data:"
            // Do NOT trim — tokens can have meaningful whitespace (e.g., " the")
            const raw = line.slice(5);
            data = raw.startsWith(" ") ? raw.slice(1) : raw;
          }
        }

        switch (eventType) {
          case "text":
            callbacks.onTextDelta(data);
            break;
          case "tool_processing":
            callbacks.onToolProcessing?.();
            break;
          case "message_complete":
            result = JSON.parse(data) as ChatResponse;
            break;
          case "error":
            throw new ApiError(500, data || "Streaming error");
          case "done":
            break;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!result) {
    throw new ApiError(500, "Stream ended without message_complete event");
  }

  return result;
}

/**
 * Star or unstar a conversation
 */
export async function setConversationStarred(
  conversationId: ConversationId,
  isStarred: boolean,
): Promise<Conversation> {
  return api.patch<Conversation>(`/conversations/${conversationId}/star`, {
    is_starred: isStarred,
  });
}

/**
 * Update a conversation title
 */
export async function updateConversationTitle(
  conversationId: ConversationId,
  title: string,
): Promise<Conversation> {
  return api.patch<Conversation>(`/conversations/${conversationId}`, {
    title,
  });
}

/**
 * Delete a conversation
 * Note: This endpoint may not exist in MVP - included for completeness
 */
export async function deleteConversation(
  conversationId: ConversationId,
): Promise<void> {
  return api.delete(`/conversations/${conversationId}`);
}

/**
 * Delete a message and all subsequent messages in the conversation (cascade by created_at).
 */
export async function deleteMessage(messageId: MessageId): Promise<void> {
  return api.delete(`/messages/${messageId}`);
}
